import DoneIcon from "@mui/icons-material/Done";
import ErrorIcon from "@mui/icons-material/ErrorOutline";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import {
  ClickAwayListener,
  Grow,
  Link,
  MenuItem,
  Paper,
  Popper,
} from "@mui/material";
import { format } from "date-fns";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import CountriesData from "../../../assets/data/CountriesData";
import vietnam from "../../../assets/images/flag/VN.png";
import ChevronDown10pxSvg from "../../../assets/images/icons/ChevronDown10pxSvg";
import EditIcon16pxSvgNew from "../../../assets/images/icons/EditIcon16pxSvgNew";
import avatar_default_logo from "../../../assets/images/logo/avatar-default-logo.png";
import { updateAccount } from "../../../axios/Account/AccountApi";
import { getCompany } from "../../../axios/Company/CompanyApi";
import CustomDateTimeRangePicker from "../../../components/CustomDateTimeRangePicker";
import CustomInput from "../../../components/CustomInput";
import CustomSelect from "../../../components/CustomSelect";
import {
  ControlpanelPrimaryButton,
  PrimaryButton,
  SelectCountryButton,
} from "../../../components/StyledButton";
import { CountryMenuList } from "../../../components/StyledComponents";
import { StyledLinkBlue } from "../../../components/StyledLink";
import { useAuth } from "../../../context/AuthContext";
import { useLoading } from "../../../context/LoadingContext";
import { useGetGeneralPolicyCountry } from "../../../hooks/useGeneralService";
import { EmailRegex2, PhoneRegex } from "../../../utils/regex/Regex";
import CompanyName from "../../../assets/images/icons/CompanyName";
import TaxCode from "../../../assets/images/icons/TaxCode";
import OwnerName from "../../../assets/images/icons/OwnerName";
import YourRole from "../../../assets/images/icons/YourRole";
import EmployeeID from "../../../assets/images/icons/EmployeeID";
import Email from "../../../assets/images/icons/Email";
import PhoneNumber from "../../../assets/images/icons/PhoneNumber";
import Address from "../../../assets/images/icons/Address";

const genderDataKeys = [
  { value: "Nữ", labelKey: "common.female" },
  { value: "Nam", labelKey: "common.male" },
];

const displayOrDash = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  return value;
};

const MyAccountDialogForm = () => {
  const { t } = useTranslation();
  const countriesData = CountriesData();
  const { data: countries } = useGetGeneralPolicyCountry();
  const { profile, genaralSettingDateFormat, refreshProfile } = useAuth();
  const [show, hide] = useLoading();

  const [companyInfo, setCompanyInfo] = useState({
    companyName: "",
    taxCode: "",
    ownerName: "",
  });

  const genderData = genderDataKeys.map((item) => ({
    value: item.value,
    label: t(item.labelKey),
  }));

  const [selectedPhoneCountry, setSelectedPhoneCountry] = useState({
    flag: vietnam,
    placeHolder: "093 777 4567",
    regex: PhoneRegex.vietnam,
  });

  const buildFormFromProfile = () => ({
    citizenNumber: profile.citizenIdentificationNumber || "",
    fullName: profile.fullName || "",
    email: profile.email || "",
    dateOfBirth: profile.dateOfBirth ? new Date(profile.dateOfBirth) : "",
    gender: profile.gender ? profile.gender : "",
    placeOfBirth: profile.placeOfBirth || "",
    phone: profile.telephone || "",
    zipCode: profile.zipCode || "",
    state: profile.state || "",
    country:
      countries?.find((c) => c.countryId === profile.countryId)?.alpha3Code ||
      "",
    address: profile.permanentAddress || "",
    profilePicture: profile.profilePicture || "",
    comment: profile.comment || "",
    city: profile.city || "",
    mobile: profile.telephone || "",
  });

  const [formData, setFormData] = useState(buildFormFromProfile);
  const [prevImageUrl, setPrevImageUrl] = useState(
    profile.profilePicture
      ? `data:image/png;base64,${profile.profilePicture}`
      : avatar_default_logo
  );
  const [openPhoneCountry, setOpenPhoneCountry] = useState(false);
  const [isPhoneError, setIsPhoneError] = useState(false);
  const [isPhoneSuccess, setIsPhoneSuccess] = useState(false);
  const [isEmailError, setIsEmailError] = useState(false);
  const [validationMessages, setValidationMessages] = useState({
    phone: "",
    email: "",
  });

  const anchorRefPhoneCountry = useRef(null);
  const imageInputRef = useRef(null);

  useEffect(() => {
    setFormData(buildFormFromProfile());
    setPrevImageUrl(
      profile.profilePicture
        ? `data:image/png;base64,${profile.profilePicture}`
        : avatar_default_logo
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile.userId, profile.accountId, countries]);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      country:
        countries?.find((c) => c.countryId === profile.countryId)?.alpha3Code ||
        "",
    }));
  }, [countries, profile.countryId]);

  useEffect(() => {
    const fetchCompanyInfo = async () => {
      if (!profile?.companyId) return;
      try {
        const response = await getCompany(
          profile.companyId,
          localStorage.getItem("i18nextLngId")
        );
        const list = response?.data?.data;
        const company = Array.isArray(list) ? list[0] : list;
        setCompanyInfo({
          companyName: company?.companyName || "",
          taxCode: company?.taxCode ?? "",
          ownerName: company?.ownerName ?? "",
        });
      } catch (error) {
        console.log(error);
      }
    };
    fetchCompanyInfo();
  }, [profile?.companyId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "phone") {
      const digits = String(value || "")
        .replace(/\D/g, "")
        .slice(0, 13);
      setFormData((prev) => ({
        ...prev,
        phone: digits,
        mobile: digits,
      }));
      if (/^\d{9,12}$/.test(digits)) {
        setIsPhoneSuccess(true);
        setIsPhoneError(false);
        setValidationMessages((prev) => ({ ...prev, phone: "" }));
      } else {
        setIsPhoneSuccess(false);
        setIsPhoneError(!!digits);
        setValidationMessages((prev) => ({
          ...prev,
          phone: t("common.content-024"),
        }));
      }
      return;
    }

    if (name === "email") {
      setFormData((prev) => ({ ...prev, email: value }));
      if (EmailRegex2.test(value)) {
        setIsEmailError(false);
        setValidationMessages((prev) => ({ ...prev, email: "" }));
      } else {
        setIsEmailError(true);
        setValidationMessages((prev) => ({
          ...prev,
          email: t("validation.email-invalid-format"),
        }));
      }
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDataChange = (name, newData) => {
    setFormData((prev) => ({
      ...prev,
      [name]: newData,
    }));
  };

  const handleClosePhoneMenu = (event) => {
    if (
      anchorRefPhoneCountry.current &&
      anchorRefPhoneCountry.current.contains(event.target)
    ) {
      return;
    }
    setOpenPhoneCountry(false);
  };

  const toBase64Data = (dataUrlOrBase64) => {
    if (typeof dataUrlOrBase64 !== "string") return "";
    const commaIdx = dataUrlOrBase64.indexOf(",");
    return commaIdx >= 0
      ? dataUrlOrBase64.slice(commaIdx + 1)
      : dataUrlOrBase64;
  };

  const resetDefaultPicture = () => {
    setPrevImageUrl(avatar_default_logo);
    setFormData((prev) => ({ ...prev, profilePicture: "" }));
  };

  const handleIconClick = () => {
    imageInputRef.current?.click();
  };

  const ACCEPTED_TYPES = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
  const MIN_RATIO = 0.7;
  const MAX_RATIO = 1.3;

  const handleImageChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!ACCEPTED_TYPES.includes(file.type)) {
      toast.warn(t("common.img-accept-file"));
      return;
    }

    const reader = new FileReader();
    reader.onerror = () => toast.error(t("common.img-noread"));
    reader.onloadend = () => {
      const dataUrl = reader.result;
      const img = new Image();
      img.onload = () => {
        const ratio = img.width / img.height;
        if (ratio < MIN_RATIO || ratio > MAX_RATIO) {
          toast.warn(t("common.error-img-size"));
          return;
        }
        setPrevImageUrl(dataUrl);
        setFormData((prev) => ({
          ...prev,
          profilePicture: toBase64Data(String(dataUrl)),
        }));
      };
      img.onerror = () => toast.error(t("common.img-noread"));
      img.src = dataUrl;
    };
    reader.readAsDataURL(file);
    event.target.value = "";
  };

  const handleUpdateAccount = async (e) => {
    e.preventDefault();
    const id_country = countries?.find(
      (c) => c.alpha3Code === formData.country
    )?.countryId;

    const payload = {
      ...formData,
      mobile: formData.phone,
      permanentAddress: formData.address,
      langId: localStorage.getItem("i18nextLngId"),
      country: id_country,
      accountId: profile.accountId,
      dateOfBirth: formData.dateOfBirth
        ? format(new Date(formData.dateOfBirth), "yyyy-MM-dd")
        : "",
    };

    try {
      show();
      const response = await updateAccount(payload);
      if (response.data.responseCode === 0) {
        toast.success(t("common.update-account-successfully"));
        refreshProfile();
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.log(error);
      toast.error(
        error?.response?.data?.message || t("common.network-timeout")
      );
    } finally {
      hide();
    }
  };

  const roleDetails = [
    {
      icon: <CompanyName sx={{ fontSize: 20, color: "#6B7280" }} />,
      label: t("common.company-name"),
      value: companyInfo.companyName || profile?.company || "",
    },
    {
      icon: <TaxCode sx={{ fontSize: 20, color: "#6B7280" }} />,
      label: t("common.content-139"),
      value: companyInfo.taxCode ?? "",
    },
    {
      icon: (
        <OwnerName sx={{ fontSize: 20, color: "#6B7280" }} />
      ),
      label: t("common.owner-name"),
      value: companyInfo.ownerName ?? "",
    },
    {
      icon: (
        <YourRole sx={{ fontSize: 20, color: "#6B7280" }} />
      ),
      label: t("common.your-role"),
      value: t(`roles3.${profile.role}`),
    },
    {
      icon: <EmployeeID sx={{ fontSize: 20, color: "#6B7280" }} />,
      label: t("common.employee-id"),
      value: profile.userId || "",
    },
    {
      icon: <Email sx={{ fontSize: 20, color: "#6B7280" }} />,
      label: t("common.email"),
      value: profile.email || "",
    },
    {
      icon: <PhoneNumber sx={{ fontSize: 20, color: "#6B7280" }} />,
      label: t("common.phone-number"),
      value: profile.telephone || "",
    },
    {
      icon: <Address sx={{ fontSize: 20, color: "#6B7280" }} />,
      label: t("common.address"),
      value: profile.permanentAddress || "",
    },
  ];

  return (
    <div className="my-profile flex flex-col gap-4">
      {/* Account details */}
      <form
        onSubmit={handleUpdateAccount}
        className="card bg-white rounded-2xl border border-[#E5E7EB] p-4 sm:p-6"
      >
        <div className="flex items-center justify-between gap-3 mb-6">
          <p className="text-lg font-semibold text-[#1F2937]">
            {t("common.account-details")}
          </p>
          <ControlpanelPrimaryButton
            type="submit"
            disabled={
              !!validationMessages.phone ||
              !formData.phone ||
              !!validationMessages.email
            }
          >
            <p>{t("button.update")}</p>
          </ControlpanelPrimaryButton>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
          {/* Avatar */}
          <div className="xl:col-span-3">
            <p className="text-sm text-[#171717] mb-2">
              {t("common.profile-picture")}
            </p>
            <div className="flex flex-col gap-2 items-center"><div className="relative inline-block">
              <div className="flex items-center justify-center overflow-hidden border border-gray-200 p-[5px] rounded-[4px] bg-white">
                <img
                  src={prevImageUrl}
                  alt="profile"
                  className="w-[168px] h-[170px] object-cover rounded-[4px]"
                />
              </div>
              <button
                type="button"
                onClick={handleIconClick}
                className="absolute bottom-0 right-0 w-10 h-10 rounded-xl bg-[#0057FF] hover:bg-[#00308C] flex items-center justify-center shadow-lg transition"
              >
                <EditIcon16pxSvgNew />
              </button>
              <input
                type="file"
                ref={imageInputRef}
                style={{ display: "none" }}
                onChange={handleImageChange}
                accept="image/*"
              />
            </div>
              <StyledLinkBlue
                onClick={resetDefaultPicture}
                className="cursor-pointer mt-3 inline-block"
              >
                <p className="text-sm">{t("common.reset-default")}</p>
              </StyledLinkBlue></div>

          </div>

          {/* Fields */}
          <div className="xl:col-span-9 grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-4">
            {/* Personal ID */}
            <div className="md:col-span-2">
              <label className="block text-sm text-[#171717]">
                {t("common.content-131")}
              </label>
              <div className="mt-2 relative flex items-center">
                <input
                  type="text"
                  name="citizenNumber"
                  readOnly
                  className="form-control no-spin-buttons relative z-10 pr-[130px] h-10"
                  value={formData.citizenNumber}
                  onChange={handleInputChange}
                />
                <PrimaryButton
                  type="button"
                  disabled
                  className="!absolute inset-y-0 right-0 !rounded-none !rounded-r-md !text-nowrap z-10 min-w-[120px]"
                >
                  {t("button.read-card")}
                </PrimaryButton>
              </div>
            </div>

            <CustomInput
              title={t("common.full-name")}
              inputType="text"
              name="fullName"
              readOnly
              placeholder={t("common.full-name")}
              handleInputChange={handleInputChange}
              value={formData.fullName}
            />
            <CustomInput
              title={t("common.email")}
              inputType="text"
              name="email"
              readOnly
              placeholder={t("common.email")}
              handleInputChange={handleInputChange}
              value={formData.email}
            />

            <div>
              <label className="block text-sm text-[#171717] pb-2">
                {t("common.date-of-birth")}
              </label>
              <CustomDateTimeRangePicker
                mode="single"
                disableTime
                showOnlyDate
                value={formData.dateOfBirth}
                onChange={(newDate) =>
                  handleDataChange("dateOfBirth", newDate)
                }
                minDate={undefined}
              />
            </div>
            <div>
              <label className="block text-sm text-[#171717] pb-2">
                {t("common.content-130")}
              </label>
              <CustomSelect
                name="gender"
                value={formData.gender}
                onChange={handleInputChange}
                options={genderData}
              />
            </div>

            <div className="md:col-span-2">
              <CustomInput
                title={t("common.place-of-birth")}
                inputType="text"
                name="placeOfBirth"
                placeholder={t("common.enter-place-of-birth")}
                handleInputChange={handleInputChange}
                value={formData.placeOfBirth}
              />
            </div>

            <div className="md:col-span-2">
              <CustomInput
                title={t("common.place-of-residence")}
                inputType="text"
                name="address"
                placeholder={t("common.enter-place-of-residence")}
                handleInputChange={handleInputChange}
                value={formData.address}
              />
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm text-[#171717]">
                {t("common.phone-number")} <span className="text-red-500">*</span>
              </label>
              <div className="relative mt-2">
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={13}
                  className={`form-control pl-20 h-10 ${isPhoneError ? "invalid" : ""
                    } ${isPhoneSuccess ? "success" : ""} ${(isPhoneError && validationMessages.phone) || isPhoneSuccess
                      ? "pr-12"
                      : ""
                    }`}
                  placeholder={selectedPhoneCountry.placeHolder}
                  name="phone"
                  onChange={handleInputChange}
                  value={formData.phone}
                />
                {isPhoneSuccess && (
                  <span className="absolute inset-y-0 right-0 px-3 flex items-center">
                    <DoneIcon fontSize="small" className="text-green-500" />
                  </span>
                )}
                {isPhoneError && validationMessages.phone && (
                  <span className="absolute inset-y-0 right-0 px-3 flex items-center">
                    <ErrorIcon fontSize="small" className="text-red-500" />
                  </span>
                )}
                <span className="absolute inset-y-0 left-0 flex items-center">
                  <SelectCountryButton
                    type="button"
                    disableRipple
                    onClick={() => setOpenPhoneCountry((prev) => !prev)}
                    ref={anchorRefPhoneCountry}
                  >
                    <img
                      src={selectedPhoneCountry.flag}
                      alt=""
                      className="w-[25px]"
                    />
                    <ChevronDown10pxSvg className="w-[10px] ml-2" />
                  </SelectCountryButton>
                </span>
                <Popper
                  open={openPhoneCountry}
                  anchorEl={anchorRefPhoneCountry.current}
                  placement="bottom-start"
                  transition
                  disablePortal
                  style={{ width: "100%", zIndex: 10 }}
                >
                  {({ TransitionProps, placement }) => (
                    <Grow
                      {...TransitionProps}
                      style={{
                        transformOrigin:
                          placement === "bottom" ? "left top" : "left bottom",
                      }}
                    >
                      <Paper style={{ borderRadius: 8, width: "100%" }}>
                        <ClickAwayListener onClickAway={handleClosePhoneMenu}>
                          <CountryMenuList autoFocusItem={openPhoneCountry}>
                            {countriesData.map((value, index) => (
                              <MenuItem
                                key={index}
                                onClick={(e) => {
                                  setSelectedPhoneCountry({
                                    flag: value.flag,
                                    placeHolder: value.placeHolder,
                                    regex: /^\d{9,12}$/,
                                  });
                                  handleClosePhoneMenu(e);
                                }}
                              >
                                <Link>
                                  <div className="flex items-center">
                                    <img
                                      src={value.flag}
                                      alt=""
                                      className="w-[25px]"
                                    />
                                    <p className="ml-2">
                                      {value.countries}
                                      <span className="text-[#C9C9C9] ml-2">
                                        {value.phoneCode}
                                      </span>
                                    </p>
                                  </div>
                                </Link>
                              </MenuItem>
                            ))}
                          </CountryMenuList>
                        </ClickAwayListener>
                      </Paper>
                    </Grow>
                  )}
                </Popper>
              </div>
              {isPhoneError && (
                <p className="text-[14px] text-red-500 mt-1">
                  {validationMessages.phone}
                </p>
              )}
            </div>

            <CustomInput
              title={t("common.zip-code")}
              inputType="text"
              name="zipCode"
              placeholder={t("common.enter-zip-code")}
              handleInputChange={handleInputChange}
              value={formData.zipCode}
            />

            <CustomInput
              title={t("common.state")}
              inputType="text"
              name="state"
              placeholder={t("common.state")}
              handleInputChange={handleInputChange}
              value={formData.state}
            />
            <div>
              <label className="block text-sm text-[#171717] pb-2">
                {t("common.country")}
              </label>
              <CustomSelect
                name="country"
                className="!bg-white"
                value={formData.country}
                options={countries || []}
                dataType="country"
                onChange={(e) => {
                  setFormData((prev) => ({
                    ...prev,
                    country: e.target.value,
                  }));
                }}
              />
            </div>
          </div>
        </div>
      </form>

      {/* Profile by role details */}
      <div className="card bg-white rounded-2xl border border-[#E5E7EB] p-4 sm:p-6">
        <p className="text-lg font-semibold text-[#1F2937] mb-4">
          {t("common.profile-by-role-details")}
        </p>
        <div className="flex flex-col gap-4">
          {roleDetails.map((item, index) => (
            <div
              key={index}
              className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-6"
            >
              <div className="flex items-center gap-2 shrink-0 sm:min-w-[180px]">
                <div className="shrink-0">{item.icon}</div>
                <p className="text-sm text-[#6B7280] whitespace-nowrap">
                  {item.label}
                </p>
              </div>
              <p className="text-sm font-medium text-[#1F2937] break-all pl-7 sm:pl-0">
                {displayOrDash(item.value)}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MyAccountDialogForm;
